import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Divider, IconButton, Typography } from '@mui/joy';
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ChatCircle,
  ChatsCircle,
  DotsSixVertical,
  Trash,
  X,
} from '@phosphor-icons/react';
import * as DialogPrimitives from '@radix-ui/react-dialog';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { FieldGroup } from '~/components/ui/fieldset';
import { InputField } from '~/components/ui/input';
import { CLOSED_REPORTS_STATUS_BOUNDARY } from '~/constants/reportConstants';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { InterviewQuestion } from '~/services/api/reports/types';
import { isMutationSuccess } from '~/services/api/rest-api';
import { RequestErrorBanner } from '~/services/api/rest-api-error';
import { useForm } from '~/utils/forms';

function useWrappedMutation<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Trigger extends (...args: any[]) => any,
  Result,
  TMutation extends Readonly<[Trigger, Result]>,
  TriggerParameters extends Parameters<TMutation[0]>,
  MutationResponse extends Exclude<
    Awaited<ReturnType<TMutation[0]>>,
    { error: unknown }
  >,
>(args: TMutation) {
  return {
    mutateAsync: async (
      parameters: TriggerParameters[0],
      callbacks?: {
        onSuccess?: (data: MutationResponse['data']) => void;
        onError?: (error: FetchBaseQueryError | SerializedError) => void;
      },
    ) => {
      const result = await args[0](parameters);
      if (isMutationSuccess(result)) {
        callbacks?.onSuccess?.(result.data as MutationResponse);
      } else {
        callbacks?.onError?.(
          result.error as FetchBaseQueryError | SerializedError,
        );
      }
      return result as Awaited<ReturnType<TMutation[0]>>;
    },
    state: args[1] as TMutation[1],
  };
}

const reorder = <T extends Array<unknown>>(
  list: T,
  startIndex: number,
  endIndex: number,
) => {
  const items = Array.from(list);
  const [reorderedItem] = items.splice(startIndex, 1);
  items.splice(endIndex, 0, reorderedItem);
  return items as T;
};

export default function InterviewPlanner({
  referenceId,
}: {
  referenceId: string;
}) {
  const [isEditable, setIsEditable] = useState(false);
  const { is_read_only_report: isReadOnlyReport } = useAppSelector(
    (state) => state.reportData.userAccessRoles,
  );
  const reportData = useAppSelector((state) => state.reportData.report);
  const isDisabled =
    isReadOnlyReport ||
    (reportData.status <= CLOSED_REPORTS_STATUS_BOUNDARY ? false : true);

  const intl = useIntl();
  const query = ReportService.endpoints.interviewQuestions.useQuery({
    referenceId,
  });
  const hasQuestions = (query.data ?? []).length > 0;

  return (
    <Dialog modal={false}>
      <DialogTrigger asChild>
        <Button
          color='neutral'
          variant='soft'
          startDecorator={<ChatCircle />}
          fullWidth
          disabled={isDisabled}
        >
          {intl.formatMessage({ defaultMessage: 'Interview Planner' })}
        </Button>
      </DialogTrigger>
      <DialogPrimitives.DialogPortal>
        <div className='fixed inset-0 overflow-hidden'>
          <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full'>
            <div className='pointer-events-auto relative w-screen max-w-sm '>
              <div className='flex h-full flex-col shadow-xl'>
                <DialogPrimitives.Content
                  className={clsx([
                    'h-full',
                    //
                    'data-[state=open]:animate-slideLeftAndFade',
                  ])}
                >
                  <Card sx={{ height: '100%', paddingBottom: '100px' }}>
                    <DialogHeader>
                      <nav className='flex items-center justify-end'>
                        <QueryResolver
                          query={query}
                          loadingElement={
                            <Button
                              startDecorator={
                                <ArrowClockwise className='animate-spin' />
                              }
                              disabled
                            >
                              {intl.formatMessage({
                                defaultMessage: 'Refresh page',
                              })}
                            </Button>
                          }
                        >
                          <Button
                            color='neutral'
                            variant='plain'
                            startDecorator={<ArrowCounterClockwise />}
                            onClick={() => query.refetch()}
                          >
                            {intl.formatMessage({
                              defaultMessage: 'Refresh page',
                            })}
                          </Button>
                        </QueryResolver>
                        <DialogClose asChild>
                          <Button
                            color='neutral'
                            variant='plain'
                            startDecorator={<X />}
                          >
                            {intl.formatMessage({
                              defaultMessage: 'Close',
                            })}
                          </Button>
                        </DialogClose>
                      </nav>
                    </DialogHeader>
                    <DialogTitle>
                      {intl.formatMessage({
                        defaultMessage: 'Interview Planner',
                      })}
                    </DialogTitle>
                    <Divider />
                    <div className='flex-1 pt-3 flex flex-col overflow-hidden'>
                      <Card size='md' sx={{ flex: 1, height: '100%' }}>
                        <Typography level='title-md' className='text-center'>
                          {intl.formatMessage({
                            defaultMessage: 'Interview Questions',
                          })}
                        </Typography>
                        <Divider />
                        <div className='flex-1 flex flex-col overflow-y-auto relative -mx-4 -mt-2 p-1'>
                          <QueryResolver
                            query={query}
                            strategy='stale-while-revalidate'
                          >
                            {(questions) =>
                              questions.length === 0 ? (
                                <div className='aspect-w-16 aspect-h-9 flex-1 flex items-center'>
                                  <div className='space-y-2 flex flex-col justify-center items-center'>
                                    <ChatsCircle size={40} />
                                    <Typography
                                      level='body-sm'
                                      className='text-center'
                                    >
                                      {intl.formatMessage({
                                        defaultMessage:
                                          'No interview questions yet, add now.',
                                      })}
                                    </Typography>
                                  </div>
                                </div>
                              ) : (
                                <QuestionsList
                                  questions={questions}
                                  referenceId={referenceId}
                                  isEditable={isEditable}
                                />
                              )
                            }
                          </QueryResolver>
                        </div>
                        <Divider />
                        <div className='grid gap-4 shrink-0'>
                          {isEditable && (
                            <div className='space-y-2'>
                              <div className='flex justify-end'>
                                <Button
                                  size='sm'
                                  color='neutral'
                                  variant='plain'
                                  onClick={() => setIsEditable((prev) => !prev)}
                                  startDecorator={<X />}
                                >
                                  {intl.formatMessage({
                                    defaultMessage: 'Close',
                                  })}
                                </Button>
                              </div>
                              <AddQuestionForm
                                defaultValues={{ referenceId }}
                              />
                            </div>
                          )}
                          {!hasQuestions && !isEditable && (
                            <Button
                              fullWidth
                              onClick={() => setIsEditable((prev) => !prev)}
                            >
                              {intl.formatMessage({
                                defaultMessage: 'Add Question',
                              })}
                            </Button>
                          )}
                          {hasQuestions && !isEditable && (
                            <nav className='flex gap-2 justify-center'>
                              <Button
                                color='secondary'
                                fullWidth
                                onClick={() => setIsEditable((prev) => !prev)}
                              >
                                {intl.formatMessage({
                                  defaultMessage: 'Edit Question',
                                })}
                              </Button>
                            </nav>
                          )}
                        </div>
                      </Card>
                    </div>
                  </Card>
                </DialogPrimitives.Content>
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitives.DialogPortal>
    </Dialog>
  );
}

function QuestionsList({
  questions: _questions,
  referenceId,
  isEditable = false,
}: {
  questions: Array<InterviewQuestion>;
  referenceId: string;
  isEditable?: boolean;
}) {
  const [questions, setQuestions] = useState(_questions);
  useEffect(() => {
    setQuestions(_questions);
  }, [_questions]);

  const intl = useIntl();
  const deleteQuestion = useWrappedMutation(
    ReportService.endpoints.deleteInterviewQuestion.useMutation(),
  );
  const updateQuestionOrder = useWrappedMutation(
    ReportService.endpoints.updateQuestionOrder.useMutation(),
  );

  return (
    <DragDropContext
      onDragEnd={async (result) => {
        if (!result.destination) {
          return;
        }

        if (result.destination.index === result.source.index) {
          return;
        }
        // Update the sort property for each item
        const sortedItems = reorder(
          questions,
          result.source.index,
          result.destination.index,
        ).map((item, index) => ({
          ...item,
          order: index + 1,
        }));
        setQuestions(sortedItems);
        await updateQuestionOrder.mutateAsync({
          referenceId,
          questions: sortedItems.map((item) => ({
            id: item.id,
            order: item.order,
          })),
        });
      }}
    >
      <Droppable droppableId={'questions'}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className='p-3 flex flex-col gap-2'
          >
            <RequestErrorBanner
              title={intl.formatMessage({
                defaultMessage: 'Question reorder error',
              })}
              error={updateQuestionOrder.state.error}
            />
            {questions.map((questionItem, index) => (
              <Draggable
                key={questionItem.id}
                index={index}
                draggableId={`question-${questionItem.id}`}
                isDragDisabled={!isEditable}
              >
                {(providedItem, snapshot) => (
                  <div
                    ref={providedItem.innerRef}
                    {...providedItem.draggableProps}
                    {...providedItem.dragHandleProps}
                    className={clsx(
                      snapshot.isDragging
                        ? 'ring-1 ring-offset-2 ring-brand-primary rounded'
                        : '',
                    )}
                  >
                    <div className='border rounded flex overflow-hidden w-full bg-white space-x-2'>
                      {isEditable && (
                        <button className='flex items-center px-2 bg-neutral-50 hover:bg-neutral-200 active:bg-neutral-300'>
                          <DotsSixVertical />
                        </button>
                      )}
                      <div
                        className={clsx([
                          'flex items-center justify-center',
                          isEditable && 'bg-brand-primary px-3',
                          !isEditable && 'pl-3',
                        ])}
                      >
                        <Typography
                          level='body-sm'
                          sx={{
                            color: (theme) =>
                              isEditable
                                ? theme.palette.common.white
                                : undefined,
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </div>
                      <div className='pr-3 py-3 flex-1'>
                        <Typography level='body-sm'>
                          {questionItem.question}
                        </Typography>
                      </div>
                      <div
                        className={clsx([
                          ' items-center justify-center shrink-0',
                          isEditable ? 'flex' : 'hidden',
                        ])}
                      >
                        <IconButton
                          aria-label='Delete'
                          color='danger'
                          onClick={async () => {
                            await deleteQuestion.mutateAsync({
                              questionId: questionItem.id,
                              referenceId: questionItem.report,
                            });
                          }}
                          loading={
                            deleteQuestion.state.originalArgs?.questionId ===
                              questionItem.id && deleteQuestion.state.isLoading
                          }
                          disabled={deleteQuestion.state.isLoading}
                        >
                          <Trash weight='fill' />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

const addQuestionSchema = z.object({
  referenceId: z.string().trim().min(1),
  question: z.string().trim().min(1),
  isCreating: z.boolean(),
});
function AddQuestionForm({
  defaultValues,
}: {
  defaultValues?: Partial<z.infer<typeof addQuestionSchema>>;
}) {
  const intl = useIntl();
  const form = useForm({
    schema: addQuestionSchema,
    defaultValues: { ...defaultValues, question: '', isCreating: false },
  });
  const createQuestion = useWrappedMutation(
    ReportService.endpoints.createInterviewQuestion.useMutation(),
  );
  const onSubmitHandler = form.handleSubmit(async (data) => {
    const result = await createQuestion.mutateAsync({
      referenceId: data.referenceId,
      questions: [{ question: data.question }],
    });
    if (isMutationSuccess(result)) {
      form.reset();
    }
  });
  const isSubmitting = form.formState.isSubmitting;

  return (
    <form onSubmit={onSubmitHandler} className='relative space-y-4'>
      <RequestErrorBanner error={createQuestion.state.error} />
      <FieldGroup>
        <InputField
          control={form.control}
          field='question'
          disabled={isSubmitting}
          aria-label={intl.formatMessage({ defaultMessage: 'Question' })}
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter new question',
          })}
        />
      </FieldGroup>
      <div className='flex justify-end'>
        <Button type='submit' color='secondary' loading={isSubmitting}>
          {intl.formatMessage({ defaultMessage: 'Add Question' })}
        </Button>
      </div>
    </form>
  );
}
